/* eslint-disable i18next/no-literal-string */
import type { WheelEvent } from 'react';
import { Controller, type ControllerProps, useFormContext } from 'react-hook-form';
import { TextField as UiTextField, type TextFieldProps as UiTextFieldProps } from 'ui';

export type TextFieldProps = Omit<UiTextFieldProps, 'name'> & {
  'data-testid'?: string;
  name: string;
  rules?: ControllerProps['rules'];
};

export const TextField = ({
  name,
  label,
  placeholder,
  onBlur,
  required = false,
  disabled = false,
  'data-testid': dataTestId,
  helperText,
  size = 'small',
  rules = {},
  onChange,
  slotProps,
  ...rest
}: TextFieldProps) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      rules={{
        ...(required && { required: 'This field is required' }),
        ...rules,
      }}
      control={control}
      render={({ field, fieldState }) => {
        const { error } = fieldState;

        const baseInputProps = {
          ...(typeof slotProps?.htmlInput === 'object' ? slotProps.htmlInput : {}),
        };
        // Prevents number input from changing value on scroll
        const wheelSafeInputProps: typeof baseInputProps =
          rest.type === 'number'
            ? {
                ...baseInputProps,
                onWheel: (event: WheelEvent<HTMLInputElement>) => {
                  if (typeof baseInputProps.onWheel === 'function') {
                    (baseInputProps.onWheel as (e: WheelEvent<HTMLInputElement>) => void)(event);
                  }
                  if (!event.defaultPrevented) {
                    event.currentTarget.blur();
                  }
                },
              }
            : baseInputProps;

        const mergedInputProps = {
          ...wheelSafeInputProps,
          ...(dataTestId != null ? { 'data-testid': dataTestId } : {}),
        } as const;

        return (
          <UiTextField
            fullWidth
            required={required}
            label={label}
            id={name}
            placeholder={placeholder}
            error={Boolean(error)}
            helperText={error?.message || helperText}
            value={field.value ?? ''}
            onChange={(event) => {
              field.onChange(
                rest.type === 'number' && event.target.value ? Number(event.target.value) : event.target.value
              );
              onChange?.(event);
            }}
            name={name}
            inputRef={field.ref}
            {...(onBlur && { onBlur })}
            disabled={disabled}
            size={size}
            slotProps={{
              ...slotProps,
              htmlInput: mergedInputProps,
            }}
            {...rest}
          />
        );
      }}
    />
  );
};
